export const MIC_PAIRING_PURPOSE = 'mic-pairing' as const;

export const BINARY_OP = {
  level: 0x01,
  chunk: 0x02,
  audioFinal: 0x03,
} as const;

export const LEVEL_BIN_COUNT = 48;
export const MAX_CHUNK_BYTES = 256 * 1024;
export const MIC_RECORDING_HARD_LIMIT_MS = 90 * 60 * 1000;

export type VadKind = 'processed' | 'skipped' | 'no_voice';

export interface AudioFinalMeta {
  mime: string;
  vad: VadKind;
  durationSec: number;
}

export function encodeLevelBinary(bins: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(1 + bins.length);
  out[0] = BINARY_OP.level;
  out.set(bins, 1);
  return out;
}

export function encodeChunkBinary(chunk: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(1 + chunk.length);
  out[0] = BINARY_OP.chunk;
  out.set(chunk, 1);
  return out;
}

export function encodeAudioFinalBinary(meta: AudioFinalMeta, audio: Uint8Array): Uint8Array<ArrayBuffer> {
  const metaBytes = new TextEncoder().encode(JSON.stringify(meta));
  if (metaBytes.length > 0xffff) {
    throw new Error('audio_final meta too large');
  }
  const out = new Uint8Array(1 + 2 + metaBytes.length + audio.length);
  out[0] = BINARY_OP.audioFinal;
  out[1] = (metaBytes.length >> 8) & 0xff;
  out[2] = metaBytes.length & 0xff;
  out.set(metaBytes, 3);
  out.set(audio, 3 + metaBytes.length);
  return out;
}

export function decodeAudioFinalBinary(
  data: Uint8Array,
): { meta: AudioFinalMeta; audio: Uint8Array } | null {
  if (data.length < 3 || data[0] !== BINARY_OP.audioFinal) return null;
  const metaLen = (data[1] << 8) | data[2];
  const metaEnd = 3 + metaLen;
  if (data.length < metaEnd) return null;
  try {
    const parsed = JSON.parse(new TextDecoder().decode(data.subarray(3, metaEnd))) as AudioFinalMeta;
    if (typeof parsed.mime !== 'string') return null;
    // slice (no subarray) so the caller owns a standalone buffer for the Blob.
    return { meta: parsed, audio: data.slice(metaEnd) };
  } catch {
    return null;
  }
}

/** Compacta el espectro del AnalyserNode a 48 barras (banda de voz). */
export function binsFromAnalyser(analyser: AnalyserNode, dest: Uint8Array): void {
  const freq = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(freq);
  const voiceEnd = Math.min(freq.length, 96);
  const step = voiceEnd / LEVEL_BIN_COUNT;
  for (let i = 0; i < LEVEL_BIN_COUNT; i++) {
    const start = Math.floor(i * step);
    const end = Math.max(start + 1, Math.floor((i + 1) * step));
    let sum = 0;
    for (let j = start; j < end && j < voiceEnd; j++) sum += freq[j];
    dest[i] = Math.round(sum / (end - start));
  }
}

export function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported?.(candidate)) return candidate;
  }
  return undefined;
}

export function splitIntoChunks(bytes: Uint8Array, maxBytes: number): Uint8Array[] {
  if (bytes.length === 0) return [];
  if (bytes.length <= maxBytes) return [bytes];
  const parts: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.length; offset += maxBytes) {
    parts.push(bytes.subarray(offset, Math.min(offset + maxBytes, bytes.length)));
  }
  return parts;
}
